import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Avatar,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Warehouse,
  ShoppingCart,
  Calendar,
  FileText,
  Users,
  UserCog,
  MapPin,
  Briefcase,
  Settings,
  LogOut,
} from 'lucide-react';
import { useMobile } from '../../../hooks/useMobile';
import { useMobileNavigation } from '../../../contexts/MobileNavigationContext';
import { mobileTokens } from '../../../theme/mobileTokens';

/**
 * Navigation Menu Item
 */
interface MenuItem {
  label: string;
  path: string;
  icon: React.ElementType;
  divider?: boolean;
}

/**
 * MobileDrawer Props
 */
interface MobileDrawerProps {
  /** Control drawer open state externally */
  open?: boolean;
  /** Callback when drawer should close */
  onClose?: () => void;
  /** User's full name */
  userName?: string;
  /** User's email */
  userEmail?: string;
  /** User's avatar URL */
  userAvatar?: string;
  /** Callback when logout is clicked */
  onLogout?: () => void;
}

/**
 * Extract initials from user's full name
 */
const getInitials = (name: string): string => {
  if (!name) return '';

  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  return (
    parts[0].charAt(0).toUpperCase() +
    parts[parts.length - 1].charAt(0).toUpperCase()
  );
};

/**
 * Navigation menu items configuration
 */
const MENU_ITEMS: MenuItem[] = [
  // Primary Navigation
  {
    label: 'Tableau de bord',
    path: '/app/dashboard',
    icon: LayoutDashboard,
  },
  {
    label: 'Produits',
    path: '/app/products',
    icon: Package,
  },
  {
    label: 'Inventaire',
    path: '/app/inventory',
    icon: Warehouse,
  },
  {
    label: 'Ventes',
    path: '/app/sales',
    icon: ShoppingCart,
    divider: true,
  },
  // Orders Section
  {
    label: 'Réservations',
    path: '/app/reservations',
    icon: Calendar,
    divider: true,
  },
  // Finance Section
  {
    label: 'Factures',
    path: '/app/invoices',
    icon: FileText,
  },
  {
    label: 'B2B / Devis',
    path: '/app/quotes',
    icon: Briefcase,
    divider: true,
  },
  // Management Section
  {
    label: 'Clients',
    path: '/app/customers',
    icon: Users,
  },
  {
    label: 'Personnel',
    path: '/app/staff',
    icon: UserCog,
  },
  {
    label: 'Emplacements',
    path: '/app/locations',
    icon: MapPin,
    divider: true,
  },
];

/**
 * MobileDrawer Component
 *
 * Swipeable side navigation drawer for mobile devices.
 * Displays user profile and complete navigation menu.
 *
 * Features:
 * - Swipeable (close by swiping right)
 * - User profile section with avatar
 * - Grouped navigation sections
 * - Active route highlighting
 * - Backdrop click to close
 * - Focus trap when open
 * - Keyboard navigation
 * - Responsive (mobile only <768px)
 *
 * @example
 * ```tsx
 * <MobileDrawer
 *   open={drawerOpen}
 *   onClose={() => setDrawerOpen(false)}
 *   userName="John Doe"
 *   userEmail="john@example.com"
 *   onLogout={handleLogout}
 * />
 * ```
 */
export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open: externalOpen,
  onClose,
  userName = '',
  userEmail = '',
  userAvatar,
  onLogout,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobile();
  const { drawerOpen, closeDrawer } = useMobileNavigation();

  // Don't render on desktop/tablet
  if (!isMobile) {
    return null;
  }

  // Use external open prop if provided, otherwise use context
  const isOpen = externalOpen !== undefined ? externalOpen : drawerOpen;

  /**
   * Handle drawer close
   */
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      closeDrawer();
    }
  };

  /**
   * Handle menu item click
   */
  const handleItemClick = (path: string) => {
    navigate(path);
    handleClose();
  };

  /**
   * Handle logout click
   */
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    handleClose();
  };

  /**
   * Check if menu item is active
   */
  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Get user initials for avatar
  const initials = getInitials(userName);

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: mobileTokens.drawer.width,
          maxWidth: mobileTokens.drawer.maxWidth,
        },
      }}
      elevation={16}
    >
      {/* User Profile Section */}
      <Box
        sx={{
          padding: theme.spacing(3, 2),
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: theme.palette.primary.contrastText,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            src={userAvatar}
            sx={{
              width: 56,
              height: 56,
              bgcolor: theme.palette.secondary.main,
              fontSize: '20px',
              fontWeight: 600,
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {userName || 'Utilisateur'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {userEmail || ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <List
        sx={{
          flex: 1,
          overflowY: 'auto',
          paddingTop: theme.spacing(1),
        }}
      >
        {MENU_ITEMS.map((item) => {
          const IconComponent = item.icon;
          const active = isActive(item.path);

          return (
            <React.Fragment key={item.path}>
              <ListItem disablePadding>
                <ListItemButton
                  role="menuitem"
                  selected={active}
                  onClick={() => handleItemClick(item.path)}
                  sx={{
                    minHeight: mobileTokens.touchTargets.min,
                    paddingY: theme.spacing(1.5),
                    paddingX: theme.spacing(2),
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.action.selected,
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        backgroundColor: theme.palette.action.selected,
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: active ? theme.palette.primary.main : 'inherit',
                    }}
                  >
                    <IconComponent size={24} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 15,
                      fontWeight: active ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
              {item.divider && <Divider sx={{ marginY: theme.spacing(1) }} />}
            </React.Fragment>
          );
        })}
      </List>

      <Divider />

      {/* Bottom Section - Settings & Logout */}
      <List>
        <ListItem disablePadding>
          <ListItemButton
            role="menuitem"
            selected={isActive('/app/settings')}
            onClick={() => handleItemClick('/app/settings')}
            sx={{
              minHeight: mobileTokens.touchTargets.min,
              paddingY: theme.spacing(1.5),
              paddingX: theme.spacing(2),
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Settings size={24} />
            </ListItemIcon>
            <ListItemText primary="Paramètres" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            role="menuitem"
            onClick={handleLogout}
            sx={{
              minHeight: mobileTokens.touchTargets.min,
              paddingY: theme.spacing(1.5),
              paddingX: theme.spacing(2),
              color: theme.palette.error.main,
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              <LogOut size={24} />
            </ListItemIcon>
            <ListItemText primary="Déconnexion" />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
};
